import React, { useState, useEffect, useCallback } from "react";
import { Mail, Send, CheckCircle2, XCircle, RefreshCw, Clock } from "lucide-react";
import { PageHeader, Button, Card, Badge, Input, Spinner } from "../lib/ui";
import { useToast } from "../lib/toast";
import { useT } from "../lib/i18n";
import api from "../lib/api";

const STATUS_TONE = { sent: "success", failed: "danger", skipped: "warn" };

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return ts; }
}

export default function EmailSettings() {
  const { t } = useT();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications/email-status");
      setData(data);
    } catch (e) {
      toast.error(t("Failed to load email status"));
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => { load(); }, [load]);

  const sendTest = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post("/notifications/test-email", testTo ? { to: testTo } : {});
      if (res.ok) toast.success(`${t("Test email sent to")} ${(res.to || []).join(", ")}`);
      else toast.error(res.detail || `${t("Could not send")} (${res.status || "error"})`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Could not send"));
    } finally {
      setSending(false);
    }
  };

  if (loading && !data) return <div className="flex justify-center py-20"><Spinner /></div>;

  const enabled = data?.enabled;

  return (
    <div className="animate-fade-up space-y-5" data-testid="email-settings-page">
      <PageHeader title={t("Email Notifications")} subtitle={t("Resend provider status, sender & delivery logs")} icon={Mail}>
        <Button variant="outline" onClick={load} data-testid="email-refresh-btn"><RefreshCw size={16} /> {t("Refresh")}</Button>
      </PageHeader>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Provider status */}
        <Card className="p-5" data-testid="email-provider-card">
          <p className="mb-3 text-xs font-bold uppercase tracking-wide text-ink-mute">{t("Email Provider Status")}</p>
          <div className="flex items-center gap-3">
            {enabled
              ? <Badge tone="success" data-testid="email-status-badge"><CheckCircle2 size={14} /> {t("Active")} — Resend</Badge>
              : <Badge tone="warn" data-testid="email-status-badge"><XCircle size={14} /> {t("Not configured")}</Badge>}
          </div>
          {!enabled && (
            <p className="mt-3 text-sm text-ink-soft" data-testid="email-disabled-hint">
              {t("Set the RESEND_API_KEY secret in your deployment to enable sending. Sender & service are ready.")}
            </p>
          )}
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between gap-4"><span className="text-ink-mute">{t("Sender Email")}</span><span className="font-semibold text-ink" data-testid="email-sender-email">{data?.from_email || "—"}</span></div>
            <div className="flex justify-between gap-4"><span className="text-ink-mute">{t("Sender Name")}</span><span className="font-semibold text-ink" data-testid="email-sender-name">{data?.from_name || "—"}</span></div>
            <div className="flex justify-between gap-4"><span className="text-ink-mute">{t("Last Email Sent")}</span><span className="font-semibold text-ink" data-testid="email-last-sent">{data?.last_sent ? fmt(data.last_sent.created_at) : t("Never")}</span></div>
          </div>
        </Card>

        {/* Send test */}
        <Card className="p-5" data-testid="email-test-card">
          <p className="mb-3 text-xs font-bold uppercase tracking-wide text-ink-mute">{t("Send Test Email")}</p>
          <p className="mb-3 text-sm text-ink-soft">{t("Leave blank to send to your own profile email.")}</p>
          <Input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@example.com"
            data-testid="email-test-to-input"
          />
          <Button className="mt-3" onClick={sendTest} loading={sending} disabled={!enabled} data-testid="email-send-test-btn">
            <Send size={16} /> {t("Send Test Email")}
          </Button>
          {!enabled && <p className="mt-2 text-xs text-ink-mute">{t("Disabled until RESEND_API_KEY is configured.")}</p>}
        </Card>
      </div>

      {/* Recent logs */}
      <Card className="overflow-hidden" data-testid="email-logs-card">
        <div className="border-b border-line px-5 py-3">
          <p className="text-xs font-bold uppercase tracking-wide text-ink-mute">{t("Recent Email Logs")}</p>
        </div>
        {(!data?.recent || data.recent.length === 0) ? (
          <div className="px-5 py-10 text-center text-sm text-ink-mute" data-testid="email-logs-empty">{t("No email activity yet.")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="email-logs-table">
              <thead>
                <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-mute">
                  <th className="px-5 py-2.5 font-semibold"><Clock size={12} className="inline" /> {t("Time")}</th>
                  <th className="px-3 py-2.5 font-semibold">{t("Event")}</th>
                  <th className="px-3 py-2.5 font-semibold">{t("Recipient")}</th>
                  <th className="px-3 py-2.5 font-semibold">{t("Status")}</th>
                  <th className="px-5 py-2.5 font-semibold">{t("Detail")}</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.map((r) => (
                  <tr key={r.id} className="border-b border-line/60" data-testid={`email-log-row-${r.id}`}>
                    <td className="whitespace-nowrap px-5 py-2.5 text-ink-soft">{fmt(r.created_at)}</td>
                    <td className="px-3 py-2.5 font-medium text-ink">{r.event || "—"}</td>
                    <td className="px-3 py-2.5 text-ink-soft">{(r.to || []).join(", ") || "—"}</td>
                    <td className="px-3 py-2.5"><Badge tone={STATUS_TONE[r.status] || "mute"}>{t(r.status)}</Badge></td>
                    <td className="px-5 py-2.5 text-xs text-ink-mute">{r.error || r.email_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
